import { apiFetch } from '@sdc/auth';
import { useEffect, useState } from 'react';
import { parseAbsences, type Absence } from '../../shared/absence';
import { PEOPLE_API_ORIGIN } from '../config';

export type AbsenceState =
  /** Keine Quelle konfiguriert – Panel zeigt „Anbindung folgt". */
  | { status: 'unconfigured' }
  | { status: 'loading' }
  | { status: 'ready'; absences: Absence[] }
  | { status: 'error'; message: string };

/**
 * „Heute abwesend" kommt aus der People-App (`/api/absences-today`), nicht aus
 * dem Identity-Projekt: die Abwesenheiten liegen in Zoho People und werden dort
 * gespiegelt. Authentifiziert wird mit demselben Identity-JWT wie überall –
 * `apiFetch` hängt es an, auch cross-origin (die People-Function hat eine
 * CORS-Allowlist für den Hub).
 */
export function useAbsences(): AbsenceState {
  const [state, setState] = useState<AbsenceState>(() =>
    PEOPLE_API_ORIGIN ? { status: 'loading' } : { status: 'unconfigured' },
  );

  useEffect(() => {
    if (!PEOPLE_API_ORIGIN) return;
    let active = true;

    void (async () => {
      try {
        const res = await apiFetch(`${PEOPLE_API_ORIGIN}/api/absences-today`);
        if (!active) return;

        // Endpoint noch nicht ausgerollt: kein Fehler, sondern „folgt".
        if (res.status === 404) {
          setState({ status: 'unconfigured' });
          return;
        }
        if (!res.ok) {
          setState({ status: 'error', message: `People antwortet ${res.status}` });
          return;
        }
        setState({ status: 'ready', absences: parseAbsences(await res.json()) });
      } catch {
        if (active) setState({ status: 'error', message: 'People ist nicht erreichbar' });
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  return state;
}
